import { EventEmitter } from 'events';
/**  StylingProvider interface exposes theme and styling properties.
 *   To apply theming, and application creates and sets property/values on a StylingProvider object
 *   and passes the object in the app scope when loading the “container”, i.e., a fluid document.
 *
 *   These property/values represent the application theme definitions and/or text style definitions.
 *   Each property specifies an aspect of the visual style of a given UI element.
 *
 *   Known theme properties (e.g. tokens) are defined in the interface ThemeProperties */

declare module '@fluidframework/core-interfaces' {
  // tslint:disable-next-line:interface-name
  export interface IFluidObject extends Readonly<Partial<ProvideStylingProvider>> {}
}

export interface ProvideStylingProvider {
  readonly StylingProvider: StylingProvider;
}

/** StyleCssVariable defines a CSS variable property. The property might have a fallback value,
 * the value of the property if a value has not been defined for the variable. */
export interface StyleCssVariable {
  customPropertyName: string;
  fallbackValue?: string | number | boolean;
}

/** StyleProperty defines the name of the style property and the CSS property name and value of the property.
 * The name is recommended to be the camelCased JSX name of the CSS property.
 * The cssPropertyName is recommended to be the CSS javascript hyphen name of the property.
 */
export interface StyleProperty {
  name: string;
  cssPropertyName: string;
  value: string | number | boolean | StyleCssVariable;
}

/** ThemeProperty defines the name of the known theme property (defined in ThemeProperties interface)
 *  and application defined value of the property.
 */
export interface ThemeProperty {
  name: string;
  value: string;
}

/** StyleDefinition defines the name of the style and its defined properties */
export interface TextStyleDefinition {
  name: string;
  properties: StyleProperty[];
}

/** TextStyle defines a map of text style name and it's corresponding collection of property names and
 * values.  */
export type TextStyles = Map<string, StyleProperty[]> | undefined;

/** ThemePropertyMap defines a map of known theme property name (defined in ThemeProperties interface)
 * and the corresponding provided theme value. */
export type ThemeProperties = Map<string, string> | undefined;

// StylingProvider interface exposes the supplied styling definitions.
// This interface is available on the scope object for use by consumers to read
// styling information provided by the application and/or other styling providers.

// Methods setThemeProperties and setTextStyles are used to set styling information.
// These methods are intended to be used by the application, or any other object, that provides
// styling information.
export interface StylingProvider extends ProvideStylingProvider, EventEmitter {
  /** A map of defined theme properties css variable name and its corresponding value. */
  readonly themeProperties: ThemeProperties;
  /** A map of text style name to it's corresponding collection of property names and values. */
  readonly textStyles: TextStyles;

  /** Implementations of this interface are expected to fire 'themePropertiesChanged' on change of
   *  themeProperties with the argument of the previous ThemeProperties. */
  setThemeProperties(properties: ThemeProperties): void;

  /** Implementations of this interface are expected to fire 'textStylesChanged' on change of textStyles
   * with the argument of the previous TextStyles.
   */
  setTextStyles(styles: TextStyles): void;
}

/**
 * List of events that StylingProvider can fire and components or
 * ContainerStylingService can listen to
 */
export enum StylingProviderEvents {
  themePropertiesChanged = 'themePropertiesChanged',
  textStylesChanged = 'textStylesChanged'
}
